import React, { Suspense, useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Home from "@/pages/Home";

// Performance — every non-home route is code-split. The site previously
// shipped ONE 1.08 MB JavaScript bundle (the 1,500-line calculator, the
// markdown renderer for blog posts, the admin editor, the energy-plans table…)
// to every visitor before the home page could paint. Each lazy page now
// downloads only when its route is first visited.
const lazyPage = (loader) => React.lazy(loader);
const About = lazyPage(() => import("@/pages/About"));
const Services = lazyPage(() => import("@/pages/Services"));
const ServiceDetail = lazyPage(() => import("@/pages/ServiceDetail"));
const Contact = lazyPage(() => import("@/pages/Contact"));
const LearningCentre = lazyPage(() => import("@/pages/LearningCentre"));
const ContentPage = lazyPage(() => import("@/pages/ContentPage"));
const Calculator = lazyPage(() => import("@/pages/Calculator"));
const LocationPage = lazyPage(() => import("@/pages/LocationPage"));
const SuburbPage = lazyPage(() => import("@/pages/SuburbPage"));
const AdminLogin = lazyPage(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazyPage(() => import("@/pages/AdminDashboard"));
const AdminPostEditor = lazyPage(() => import("@/pages/AdminPostEditor"));
const NotFound = lazyPage(() => import("@/pages/NotFound"));
const ThankYou = lazyPage(() => import("@/pages/ThankYou"));
const FacebookLanding = lazyPage(() => import("@/pages/FacebookLanding"));
const EnergySwitch = lazyPage(() => import("@/pages/EnergySwitch"));
const AffiliateDisclosure = lazyPage(() => import("@/pages/AffiliateDisclosure"));
const ExportLimits = lazyPage(() => import("@/pages/ExportLimits"));
const ExportLimitsArea = lazyPage(() => import("@/pages/ExportLimitsArea"));
const SolarChecker = lazyPage(() => import("@/pages/SolarChecker"));
const Finance = lazyPage(() => import("@/pages/Finance"));
const FinanceApplication = lazyPage(() => import("@/pages/FinanceApplication"));
const RooftopReport = lazyPage(() => import("@/pages/RooftopReport"));
import { CoreValuesProvider } from "@/components/site/CoreValuesModal";
import { SiteGate } from "@/components/site/SiteGate";
// sonner's <Toaster/> is mounted lazily once the browser is idle so its
// runtime never sits on the first-paint critical path (see lib/toast.js).
const Toaster = React.lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));
import { initClickTracking } from "@/lib/clickTracking";
// Phase 37 - store gclid / utm_* / landing referrer on arrival so every
// lead form can send them even after the visitor navigates away from the
// landing URL (see lib/attribution.js).
import { initAttribution } from "@/lib/attribution";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

// Phase 9: fire Meta Pixel + GA PageView on every SPA navigation. The initial
// PageView is fired by the <script> in public/index.html on first paint; this
// hook only handles client-side route changes.
//
// NOTE — Phase 17 revised (Feb 2026): the Google Ads virtual-pageview approach
// (`gtag('config', AW-..., {page_path})` on every route change) was replaced
// with Google Tag Manager. GTM listens on dataLayer events pushed from the
// form handlers (quote_form_submit_click / _success and calculator_lead_*),
// so this tracker only needs the pre-existing GA4 page_view + Meta PageView
// behavior. Google Ads conversion tags are configured inside GTM going forward.
function RouteChangeTracker() {
  const { pathname, search } = useLocation();
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      if (window.fbq) window.fbq("track", "PageView");
    } catch (_) { /* ignore */ }
    try {
      if (window.gtag) {
        window.gtag("event", "page_view", { page_path: pathname + search });
      }
    } catch (_) { /* ignore */ }
  }, [pathname, search]);
  return null;
}

function IdleToaster() {
  const [ready, setReady] = React.useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(go, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(go, 2500);
    return () => clearTimeout(t);
  }, []);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster richColors position="top-center" closeButton />
    </Suspense>
  );
}

// Blank, full-height placeholder shown for the few hundred ms a lazy route
// chunk takes to arrive on client-side navigation. Keeps the page height so
// the footer does not flash into the viewport.
function RouteFallback() {
  return <div className="min-h-screen bg-white" aria-hidden="true" />;
}

// Legacy /blog/:slug → /learning-centre/:slug (preserves slug + querystring)
function RedirectBlogPost() {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/learning-centre/${slug}${search}`} replace />;
}

// Phase 38 — the LoanOptions widget navigates to `${sourceUrl}/application`;
// keep the application under /finance and preserve its querystring
// (utm_*, gclid, partnerId are re-read there).
function RedirectToFinanceApplication() {
  const { search } = useLocation();
  return <Navigate to={`/finance/application${search}`} replace />;
}

function App() {
  // Phase 9 — install global click delegation for mailto:/tel: tracking
  // exactly once, at app boot. Idempotent (clickTracking.js short-circuits
  // on re-init), so safe under React 18 strict-mode double-effect.
  useEffect(() => {
    initAttribution();
    initClickTracking();
  }, []);

  return (
    <HelmetProvider>
      <SiteGate>
        <div className="App">
          <BrowserRouter>
            <ScrollManager />
            <RouteChangeTracker />
            <CoreValuesProvider>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                {/* Phase 19 — dedicated service-detail landing pages.
                    The dynamic `:slug` route below the three explicit ones
                    means unknown slugs render our NotFound stub (via the
                    ServiceDetail component's fallback branch). */}
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/learning-centre" element={<LearningCentre />} />
                <Route path="/learning-centre/:slug" element={<ContentPage />} />
                {/* Phase 6: legacy /blog → /learning-centre 301 redirects */}
                <Route
                  path="/blog"
                  element={<Navigate to="/learning-centre" replace />}
                />
                <Route path="/blog/:slug" element={<RedirectBlogPost />} />
                <Route path="/solar-calculator" element={<Calculator />} />
                {/* Phase 36 — Google Solar API roof + savings checker. */}
                <Route path="/solar-panel-checker" element={<SolarChecker />} />
                {/* Phase 16 — 5 NSW location landing pages removed (not in
                    actual service area). Explicit React Router redirects so
                    SPA-served preview routes also 301 → / via the route. The
                    backend Phase 13 redirect middleware handles the same
                    paths in production where FastAPI sees them first. */}
                <Route path="/solar-installers/sydney" element={<Navigate to="/" replace />} />
                <Route path="/solar-installers/newcastle" element={<Navigate to="/" replace />} />
                <Route path="/solar-installers/wollongong" element={<Navigate to="/" replace />} />
                <Route path="/solar-installers/orange" element={<Navigate to="/" replace />} />
                <Route path="/solar-installers/bathurst" element={<Navigate to="/" replace />} />
                <Route path="/solar-installers/:slug" element={<LocationPage />} />
                {/* Phase 32 — suburb landing pages nested under each hub. */}
                <Route path="/solar-installers/:hub/:suburb" element={<SuburbPage />} />
                {/* Phase 9: conversion-tracking + paid-landing routes */}
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/facebook-landing" element={<FacebookLanding />} />
                {/* Phase 23 — Canstar affiliate energy-switch page. Noindexed,
                    absent from NAV_LINKS + sitemap, disallowed in robots.txt.
                    Reached only via direct / campaign links. */}
                <Route path="/energy-switch" element={<EnergySwitch />} />
                {/* Phase 31 — affiliate / tracking / privacy notice for the
                    energy-switch offer. Indexable, linked from the disclosure
                    strip and the site footer. */}
                <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
                {/* Phase 34 — solar export checker + per-area capacity pages. */}
                {/* Phase 39 — QVolt Rooftop Report (annual install-trend report). */}
                <Route path="/rooftop-report" element={<RooftopReport />} />
                <Route path="/solar-barometer" element={<Navigate to="/rooftop-report" replace />} />
                <Route path="/solar-export-limits" element={<ExportLimits />} />
                <Route path="/solar-export-limits/:slug" element={<ExportLimitsArea />} />
                {/* Phase 38 — LoanOptions.ai finance widget. Noindexed, absent
                    from nav + sitemap, disallowed in robots.txt. The widget
                    hands off to ${sourceUrl}/application (fixed by the partner
                    config), which we fold under /finance. */}
                <Route path="/finance" element={<Finance />} />
                <Route path="/finance/application" element={<FinanceApplication />} />
                <Route path="/application" element={<RedirectToFinanceApplication />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                {/* Phase 9: /admin/posts is the soft-404 alias of the dashboard. */}
                <Route
                  path="/admin/posts"
                  element={<Navigate to="/admin" replace />}
                />
                <Route path="/admin/posts/new" element={<AdminPostEditor />} />
                <Route path="/admin/posts/:id/edit" element={<AdminPostEditor />} />
                <Route path="*" element={<NotFound heading="Page not found" />} />
              </Routes>
              </Suspense>
            </CoreValuesProvider>
          </BrowserRouter>
        </div>
        {/* Phase 15 — global sonner Toaster for form-failure notifications.
            Sits OUTSIDE BrowserRouter so toasts persist across navigation. */}
        <IdleToaster />
      </SiteGate>
    </HelmetProvider>
  );
}

export default App;
